import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useInRouterContext } from "react-router-dom";
import { Bot, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import { useTaal } from "@/contexts/TaalContext";
import { Button } from "./ui/button";

const DAGNAMEN = {
  nl: ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"],
  en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
};
export const BRONNAMEN = { tasq: "TASQ", fathom: "Fathom", agenda: "Agenda", mail: "Gmail", drive: "Drive" };

/** De dagbronnen van een worker als namen, in de volgorde van de kaart. PUUR. GL200-2. */
export function bronnenTekst(bronnen) {
  return (bronnen || []).map((b) => BRONNAMEN[b] || b).join(", ");
}

/** "elke maandag om 08:00", "elke werkdag om 07:45", "elke dag om 08:00". PUUR. */
export function wanneer(tijdstip, taal) {
  if (!tijdstip) return "";
  const en = taal === "en";
  const dagen = [...(tijdstip.dagen || [])].sort();
  const uur = `${String(tijdstip.uur).padStart(2, "0")}:${String(tijdstip.minuut || 0).padStart(2, "0")}`;
  const om = en ? "at" : "om";
  if (dagen.length === 7) return `${en ? "every day" : "elke dag"} ${om} ${uur}`;
  if (dagen.join() === "0,1,2,3,4") return `${en ? "every weekday" : "elke werkdag"} ${om} ${uur}`;
  const namen = dagen.map((d) => DAGNAMEN[en ? "en" : "nl"][d]);
  return `${en ? "every" : "elke"} ${namen.join(en ? " and " : " en ")} ${om} ${uur}`;
}

/**
 * DW1 en DW2, 24 oktober 2026: een digitale worker op glas, en pas na Bevestig een agent.
 *
 * Zelfde opbouw als `AfspraakVoorstel`: de velden komen uit de opgeslagen rij (niet uit de
 * tekst die een model schreef), de kaart krijgt een WF-40-token, en de bevestiging stuurt
 * ALLE velden terug zoals ze op het scherm stonden. De server vergelijkt ze veld voor veld.
 */
export default function WorkerVoorstel({ voorstelId, onAangemaakt }) {
  const { t, taal } = useTaal();
  const inRouter = useInRouterContext();
  const [rij, setRij] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const { token, vervallen, laatVervallen, reden } = useKaartToken(
    rij?.id ? `${API}/workers/${rij.id}/toon` : "", Boolean(rij?.id && !rij.aangemaakt));

  useEffect(() => {
    let levend = true;
    axios.get(`${API}/workers/${voorstelId}`)
      .then((r) => { if (levend) setRij(r.data); })
      .catch((e) => { if (levend) setFout(e.response?.data?.detail || t("Dit voorstel kon niet worden geladen")); });
    return () => { levend = false; };
  }, [voorstelId]); // eslint-disable-line react-hooks/exhaustive-deps -- alleen bij een ander voorstel

  // GL214-5 (glasronde 0.9.214): na het aanmaken stond boven de kaart nog "Er is nog niets aangemaakt".
  // De bubbel krijgt een nieuwe zin, ook na herladen (de server weet of de agent er is).
  useEffect(() => {
    if (rij?.aangemaakt) onAangemaakt?.(t("De worker is aangemaakt en staat bij Agenten."));
  }, [rij?.aangemaakt]); // eslint-disable-line react-hooks/exhaustive-deps -- alleen bij het omslaan

  const bevestig = async () => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/workers/${rij.id}/uitvoeren`, {
        naam: rij.naam, standing_task: rij.standing_task, bronnen: rij.bronnen,
        tool_names: rij.tool_names, tijdstip: rij.tijdstip, skill_naam: rij.skill_naam, token,
      });
      setRij(r.data);
      toast.success(t("De worker staat bij Agenten"));
    } catch (e) {
      const tekst = e.response?.data?.detail || t("Het aanmaken is niet gelukt");
      setFout(tekst);
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  if (!rij) {
    return fout ? <p className="text-xs text-destructive">{fout}</p> : null;
  }
  // GL214-6 (glasronde 0.9.214): de link opent de gemaakte agent zelf, niet alleen de lijst.
  const doel = rij.agent_id ? `/agents?agent=${encodeURIComponent(rij.agent_id)}` : "/agents";
  const naarAgenten = inRouter
    ? <Link to={doel} className="underline ml-1" data-testid="worker-naar-agenten">{t("Open bij Agenten")}</Link>
    : <a href={doel} className="underline ml-1" data-testid="worker-naar-agenten">{t("Open bij Agenten")}</a>;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="worker-voorstel">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Bot className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span data-testid="worker-naam">{rij.naam}</span>
      </div>
      <p className="text-xs" data-testid="worker-opdracht">{rij.standing_task}</p>
      <p className="text-xs text-muted-foreground" data-testid="worker-tijdstip">
        {rij.tijdstip
          ? t("Draait {wanneer}, alleen als de Mac aan staat. De uitslag komt in de Postbus.",
            { wanneer: wanneer(rij.tijdstip, taal) })
          : t("Geen vast tijdstip: je start hem zelf bij Agenten.")}
      </p>
      <p className="text-xs text-muted-foreground" data-testid="worker-bronnen">
        {rij.bronnen.length || rij.tool_names.length
          ? t("Leest alleen: {bronnen}", {
            bronnen: [...rij.bronnen.map((b) => BRONNAMEN[b] || b),
              ...(rij.tool_names.length ? [t("{n} gereedschap van een genoemde koppeling", { n: rij.tool_names.length })] : [])]
              .join(", "),
          })
          : t("Leest geen koppelingen; de zin noemde er geen.")}
      </p>
      {rij.skill_naam && (
        <p className="text-xs text-muted-foreground" data-testid="worker-skill">
          {t("Met een eigen skill voor de werkwijze: {naam}", { naam: rij.skill_naam })}
        </p>
      )}
      {rij.aangemaakt ? (
        <p className="text-xs flex items-center gap-1" data-testid="worker-gemaakt">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          {t("Aangemaakt.")}
          {naarAgenten}
        </p>
      ) : (
        <Button size="sm" onClick={bevestig} disabled={bezig || !token || vervallen}
                className={vervallen ? GRIJS : ""} data-testid="worker-bevestig">
          {bezig && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          {t("Bevestig en maak de worker")}
        </Button>
      )}
      {(fout || reden) && <p className="text-xs text-destructive">{fout || reden}</p>}
    </div>
  );
}
